// Free Walking Tours
import { Link } from "react-router-dom";
import { ChevronLeft, Clock, MapPin, ExternalLink, Calendar, Users, Globe } from "lucide-react";
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";

export default function FreeTours() {
  const base = useTripBase();
  const { trip } = useTrip();
  const tours = trip?.freeTours || [];
  const CITIES = useMemo(() => {
    const set = new Set(tours.map((t) => t.city).filter(Boolean));
    return ["Todas", ...Array.from(set)];
  }, [tours]);
  const [city, setCity] = useState("Todas");
  const items = tours.filter((t) => city === "Todas" || t.city === city);

  return (
    <div className="container-app pt-6 pb-28" data-testid="freetours-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="freetours-back">
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Free Walking Tours</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">
        Tours gratuitos con guías locales. <span className="text-[hsl(var(--ink))] font-medium">Pay what you want</span> al final (~€10–15 por persona de propina).
      </p>

      <div className="card-paper p-4 mb-5" style={{ background: "hsl(var(--accent-soft))" }}>
        <p className="text-xs text-[hsl(var(--ink))] leading-relaxed">
          💡 <strong>Cómo funcionan:</strong> Reservan online (link de cada tour), aparecen en el punto de encuentro 15 min antes, el guía los reconoce con una sombrilla de color o cartel. El tour dura 2–3h y al final dejan propina según lo que les pareció. Llevan agua y calzado cómodo.
        </p>
      </div>

      {/* Acceso directo a la cuenta de FreeTour del usuario */}
      <div className="flex gap-2 mb-5">
        <a
          href="https://www.freetour.com/account/bookings/upcoming"
          target="_blank"
          rel="noreferrer"
          className="flex-1 card-paper p-3 flex items-center gap-2 active:scale-[0.99] transition-transform"
          data-testid="freetour-account-link"
        >
          <span className="text-lg">🎫</span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Mis reservas</p>
            <p className="text-xs font-bold leading-tight">Freetour.com · Mi cuenta</p>
          </div>
          <ExternalLink size={14} className="text-[hsl(var(--muted))]" />
        </a>
        <a
          href="https://www.guruwalk.com/users/bookings/upcomings"
          target="_blank"
          rel="noreferrer"
          className="card-paper p-3 flex items-center justify-center"
          aria-label="GuruWalk"
          data-testid="guruwalk-account-link"
        >
          <span className="text-lg">🚶</span>
        </a>
      </div>

      {/* City filter */}
      <div className="flex gap-2 mb-5 overflow-x-auto hide-scrollbar">
        {CITIES.map((c) => (
          <button
            key={c}
            onClick={() => setCity(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              city === c
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`freetours-city-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      <ul className="space-y-3">
        {items.map((t, i) => (
          <motion.li
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.04 * i }}
            className="card-paper p-4"
            data-testid={`freetour-${t.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="chip-ink">{t.city}</span>
              {t.jewish && <span className="chip-jewish">✡ Judío</span>}
              <span className="chip-success">Gratis · propina</span>
            </div>
            <h3 className="font-bold text-base leading-tight mb-1">{t.title}</h3>
            <p className="text-xs text-[hsl(var(--muted))] mb-3">por {t.operator}</p>

            <p className="text-sm serif text-[hsl(var(--ink-soft))] leading-[1.55] mb-3">{t.summary}</p>

            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
              <div className="flex items-start gap-1.5">
                <Clock size={12} className="text-[hsl(var(--muted))] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[hsl(var(--muted))]">Duración</p>
                  <p className="font-semibold">{t.duration}</p>
                </div>
              </div>
              <div className="flex items-start gap-1.5">
                <Calendar size={12} className="text-[hsl(var(--muted))] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[hsl(var(--muted))]">Horarios</p>
                  <p className="font-semibold">{t.times}</p>
                </div>
              </div>
              <div className="flex items-start gap-1.5">
                <Globe size={12} className="text-[hsl(var(--muted))] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[hsl(var(--muted))]">Idioma</p>
                  <p className="font-semibold">{t.language}</p>
                </div>
              </div>
              <div className="flex items-start gap-1.5">
                <Users size={12} className="text-[hsl(var(--muted))] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[hsl(var(--muted))]">Sugerido</p>
                  <p className="font-semibold">{(trip?.days || []).find((d) => d.id === t.dayRecommended)?.dayLabel || "—"}</p>
                </div>
              </div>
            </div>

            <div className="card-paper p-3 mb-2" style={{ background: "hsl(var(--bg))" }}>
              <div className="flex items-start gap-2">
                <MapPin size={12} className="text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))]">Punto de encuentro</p>
                  <p className="text-sm">{t.meeting}</p>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${t.meetingLat},${t.meetingLng}&travelmode=walking`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-[hsl(var(--accent))] inline-flex items-center gap-1 mt-1"
                    data-testid={`freetour-directions-${t.id}`}
                  >
                    Cómo llegar <ExternalLink size={10} />
                  </a>
                </div>
              </div>
            </div>

            {t.tip && (
              <p className="text-xs text-[hsl(var(--accent))] leading-relaxed mb-3">💡 {t.tip}</p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <a
                href={t.url ? (t.url.startsWith("http") ? t.url : `https://${t.url}`) : `https://www.google.com/search?q=${encodeURIComponent(`${t.operator} ${t.title} ${t.city}`)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-primary flex items-center justify-center gap-2 text-xs"
                data-testid={`freetour-book-${t.id}`}
              >
                Reservar <ExternalLink size={12} />
              </a>
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(`${t.operator} ${t.title} ${t.city} free tour booking`)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost flex items-center justify-center gap-2 text-xs border border-[hsl(var(--line))]"
                data-testid={`freetour-search-${t.id}`}
              >
                Buscar 🔍
              </a>
            </div>
          </motion.li>
        ))}
      </ul>

      <p className="text-xs text-[hsl(var(--muted))] text-center mt-6 leading-relaxed">
        Las propinas son la única paga de los guías. Si fue buen tour, propina generosa.
      </p>
    </div>
  );
}
